/**
 * The honest fallback ladder for PDP price history (FRIDGE-282).
 *
 * A price chart is a claim: "we watched this price and here is what it did."
 * The catalog cannot support that claim on most product pages, so this module
 * decides — from the real series alone — which rung a product has earned. It
 * is pure and client-safe, and it is the single place the decision is made.
 *
 * MEASURED 08/16/2026. Two counts, because they answer different questions and
 * quoting the larger one alone would overstate what the page can show.
 *
 * Across all 20,280 rows carrying a slug — the shape of the stored history,
 * joined through keepa_products, drafts included:
 *
 *   no history 7,900 (39.0%) · too-thin 5,413 (26.7%) ·
 *   no-movement 2,829 (13.9%) · chart 4,138 (20.4%)
 *
 * Across the 4,835 ACTIVE products that actually render a page, joined the way
 * the PDP itself joins (canonical_products.amazon_asin):
 *
 *   chart 104 (2.2%) · everything else 4,731 (97.8%), of which 3,363 hold no
 *   tracked history at all
 *
 * The second table is the honest one for this component. Draft rows never
 * render, and at the time of that count the PDP read history through a SINGLE
 * amazon_asin while keepa_products may hold several for one canonical product —
 * so the stored history was larger than the history the page could see.
 *
 * THAT GAP IS NOW CLOSED (FRIDGE-375, wired 08/2026). The PDP resolves every
 * mapped ASIN whose own Keepa record corroborates the canonical model. Re-measured
 * 08/22/2026 over full history, with the same lowest-per-day collapse the page
 * applies, across active pdp_eligible products:
 *
 *   products holding any tracked history            1,471
 *   clear the 14-sample-day gate — single ASIN        104
 *   clear it — multi-ASIN (what ships today)          119
 *
 * WHO OWNS "NOT ENOUGH HISTORY" — the FRIDGE-375 ordering question, settled.
 * The gate and this ladder are NOT two answers to one question; they answer two,
 * and each owns exactly one:
 *
 *   the 14-sample-day gate  owns  MAY WE MAKE A COMPARATIVE CLAIM
 *                                 ("all-time low", "$X below the 90-day average")
 *   this ladder             owns  MAY WE DRAW A LINE
 *
 * The gate runs first because its subject is money on a public page: below 14
 * tracked days there is no claim to make, so there is no module to draw into.
 * Among the 119 that clear it the ladder still decides, and still refuses —
 * measured 08/22, ONE of them holds 14+ days at a single unchanging price and is
 * correctly denied a flat line. Under the single-ASIN path that count was zero,
 * which is why this rung read as dead code; it is not.
 *
 * 'too-thin' is structurally unreachable BEHIND the gate — 14 sample days is
 * necessarily 8 or more points — and is kept deliberately, because this module is
 * the place the line decision is made and a classifier that only handles the
 * cases today's data produces is one data change away from being wrong.
 *
 * The chart is therefore the rare case, not the default, and the fallbacks are
 * the main path. Two rungs matter most:
 *
 *  • too-thin — below MIN_SPARK_POINTS real observations there is no shape to
 *    show. Two snapshots drawn as a line is a trend that was never observed.
 *    These pages state what we actually hold and stop.
 *
 *  • no-movement — 2,829 stored rows hold 8 or more observations that are ALL
 *    THE SAME PRICE. The old code drew those as a line, because it counted
 *    points and never looked at their values: a flat stroke across a chart
 *    frame, reading as a tracked trend when the honest finding is "this price
 *    has not changed". A sentence says that better than a chart, and does not
 *    invite the reader to see movement in a straight line.
 *
 *    When this was written the rung was reached by ZERO pages, because
 *    derivePriceIntelligence hides the whole module below 14 tracked sample
 *    days and no product then cleared that floor while holding exactly one
 *    distinct price. Re-measured 08/22/2026 after the multi-ASIN resolver
 *    shipped: ONE page now lands here and is correctly refused a flat line.
 *    The prediction that closing the multi-ASIN gap would put pages back on
 *    this rung was right, which is the argument for having kept it.
 *
 * What this module never does: invent a point, interpolate a gap into a value,
 * extend a series to the edge of a frame, or let a chart stand in for absent
 * data.
 */

import type { SparkPoint } from './derive'

/** Minimum real observations before a series has a shape worth drawing. */
export const MIN_CHART_POINTS = 8

export type PriceHistoryRung =
  /** Enough real observations, and the price actually moved. */
  | { kind: 'chart'; points: SparkPoint[]; distinctPrices: number; spanDays: number }
  /** Enough observations, but every one is the same price. */
  | { kind: 'no-movement'; points: SparkPoint[]; priceCents: number; spanDays: number }
  /** Too few real observations to show a shape. */
  | { kind: 'too-thin'; points: SparkPoint[] }

/**
 * Keep only points that are real: a positive integer price and a full ISO date.
 * A zero or a null price is an absent reading, never a $0 observation.
 */
export function cleanSeries(series: SparkPoint[] | null | undefined): SparkPoint[] {
  if (!Array.isArray(series)) return []
  return series.filter(
    (p): p is SparkPoint =>
      !!p &&
      typeof p.c === 'number' &&
      Number.isFinite(p.c) &&
      p.c > 0 &&
      typeof p.d === 'string' &&
      p.d.length >= 10
  )
}

/** Whole days between the first and last observation, 0 when indeterminate. */
export function spanDaysOf(points: SparkPoint[]): number {
  if (points.length < 2) return 0
  const first = Date.parse(points[0].d)
  const last = Date.parse(points[points.length - 1].d)
  if (!Number.isFinite(first) || !Number.isFinite(last)) return 0
  return Math.max(0, Math.round((last - first) / 86_400_000))
}

/**
 * Decide the rung. `minPoints` is injectable so a test can pin the boundary
 * without depending on the shipped constant.
 */
export function classifyPriceHistory(
  series: SparkPoint[] | null | undefined,
  minPoints: number = MIN_CHART_POINTS
): PriceHistoryRung {
  const points = cleanSeries(series)

  if (points.length < minPoints) return { kind: 'too-thin', points }

  const distinctPrices = new Set(points.map((p) => p.c)).size
  const spanDays = spanDaysOf(points)

  if (distinctPrices < 2) {
    return { kind: 'no-movement', points, priceCents: points[0].c, spanDays }
  }

  return { kind: 'chart', points, distinctPrices, spanDays }
}
